import type { Locale } from "./i18n";

interface AppShellContent {
  readonly serviceBadge: string;
  readonly verifiedEmptyTitle: string;
  readonly verifiedEmptyBody: string;
  readonly estimateBasis: string;
  readonly commerceNote: string;
  readonly marketplaceBadge: string;
  readonly sampleLoading: string;
  readonly sampleError: string;
  readonly webpEdit: string;
  readonly home: string;
  readonly designStudio: string;
  readonly styles: Readonly<Record<string, string>>;
  readonly districts: Readonly<Record<string, string>>;
  readonly locations: Readonly<Record<string, string>>;
}

const englishStyles: Readonly<Record<string, string>> = {
  "modern-tropical": "Modern tropical",
  "warm-modern": "Warm modern",
  "thai-contemporary": "Thai contemporary",
  "compact-living": "Compact living",
  commercial: "Commercial"
};

const englishDistricts: Readonly<Record<string, string>> = {
  Bangkok: "Bangkok", Nonthaburi: "Nonthaburi", "Pathum Thani": "Pathum Thani",
  "Samut Prakan": "Samut Prakan", Phuket: "Phuket", "Chiang Mai": "Chiang Mai"
};

const englishLocations: Readonly<Record<string, string>> = {
  Thailand: "Thailand", "Trang, Thailand": "Trang, Thailand", "Chiang Mai, Thailand": "Chiang Mai, Thailand",
  "Koh Samui, Thailand": "Koh Samui, Thailand", "Bangkok, Thailand": "Bangkok, Thailand",
  "Nakhon Ratchasima, Thailand": "Nakhon Ratchasima, Thailand"
};

const english: AppShellContent = {
  serviceBadge: "Decobaan service",
  verifiedEmptyTitle: "Verified studios are coming soon.",
  verifiedEmptyBody: "Only studios that pass Decobaan review are published here.",
  estimateBasis: "Bangkok benchmark",
  commerceNote: "Optional project sourcing · materials and furniture · vendor offers",
  marketplaceBadge: "Decobaan marketplace",
  sampleLoading: "Loading interior samples.",
  sampleError: "Interior samples are temporarily unavailable. Please try again later.",
  webpEdit: "WebP edit",
  home: "Home",
  designStudio: "3D design preview",
  styles: englishStyles,
  districts: englishDistricts,
  locations: englishLocations
};

export const appShellCopy: Readonly<Record<Locale, AppShellContent>> = {
  th: {
    serviceBadge: "บริการโดย Decobaan",
    verifiedEmptyTitle: "ทีมผู้เชี่ยวชาญที่ผ่านการตรวจสอบจะเปิดให้บริการเร็ว ๆ นี้",
    verifiedEmptyBody: "เราแสดงเฉพาะทีมที่ผ่านการตรวจสอบจาก Decobaan เท่านั้น",
    estimateBasis: "เกณฑ์ราคาในกรุงเทพฯ",
    commerceNote: "จัดหาสำหรับโปรเจกต์ตามต้องการ · วัสดุและเฟอร์นิเจอร์ · ข้อเสนอจากผู้ขาย",
    marketplaceBadge: "ตลาด Decobaan",
    sampleLoading: "กำลังโหลดตัวอย่างการตกแต่งภายใน",
    sampleError: "ไม่สามารถโหลดตัวอย่างได้ โปรดลองอีกครั้งภายหลัง",
    webpEdit: "ปรับเป็น WebP",
    home: "หน้าหลัก",
    designStudio: "ตัวอย่างการออกแบบ 3D",
    styles: { "modern-tropical": "โมเดิร์นทรอปิคอล", "warm-modern": "วอร์มโมเดิร์น", "thai-contemporary": "ไทยร่วมสมัย", "compact-living": "พื้นที่กะทัดรัด", commercial: "พื้นที่เชิงพาณิชย์" },
    districts: { Bangkok: "กรุงเทพฯ", Nonthaburi: "นนทบุรี", "Pathum Thani": "ปทุมธานี", "Samut Prakan": "สมุทรปราการ", Phuket: "ภูเก็ต", "Chiang Mai": "เชียงใหม่" },
    locations: { Thailand: "ประเทศไทย", "Trang, Thailand": "ตรัง ประเทศไทย", "Chiang Mai, Thailand": "เชียงใหม่ ประเทศไทย", "Koh Samui, Thailand": "เกาะสมุย ประเทศไทย", "Bangkok, Thailand": "กรุงเทพฯ ประเทศไทย", "Nakhon Ratchasima, Thailand": "นครราชสีมา ประเทศไทย" }
  },
  ko: {
    serviceBadge: "Decobaan 서비스", verifiedEmptyTitle: "검증된 업체를 준비하고 있습니다.", verifiedEmptyBody: "Decobaan 심사를 통과한 업체만 공개됩니다.",
    estimateBasis: "방콕 시장 기준", commerceNote: "선택형 프로젝트 조달 · 자재와 가구 · 업체별 오퍼", marketplaceBadge: "Decobaan 마켓플레이스",
    sampleLoading: "인테리어 샘플을 불러오는 중입니다.", sampleError: "인테리어 샘플을 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.", webpEdit: "WebP 편집", home: "홈", designStudio: "3D 설계 미리보기",
    styles: { "modern-tropical": "모던 트로피컬", "warm-modern": "웜 모던", "thai-contemporary": "타이 컨템포러리", "compact-living": "콤팩트 리빙", commercial: "상업 공간" },
    districts: { Bangkok: "방콕", Nonthaburi: "논타부리", "Pathum Thani": "빠툼타니", "Samut Prakan": "사뭇쁘라깐", Phuket: "푸껫", "Chiang Mai": "치앙마이" },
    locations: { Thailand: "태국", "Trang, Thailand": "뜨랑, 태국", "Chiang Mai, Thailand": "치앙마이, 태국", "Koh Samui, Thailand": "코사무이, 태국", "Bangkok, Thailand": "방콕, 태국", "Nakhon Ratchasima, Thailand": "나콘랏차시마, 태국" }
  },
  en: english,
  es: english,
  zh: english,
  ja: english
};
